import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { PRODUCTS, COMPLIANCE_FRAMEWORKS } from "../data/site";

const highlights = [
  "Automated evidence collection across your systems",
  "Control mapping & continuous gap analysis",
  "Independent auditor coordination end-to-end",
];

// Flagship product portal. A striking dark panel on the light page, so Nova
// (a dark-brand product) visually dominates. Driven by PRODUCTS data.
export const NovaShowcase = () => {
  const nova = PRODUCTS.find((p) => p.isFlagship) || PRODUCTS[0];

  return (
    <section id="nova" data-testid="nova-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-10 flex items-center gap-3">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-blue-600">
            Flagship Product
          </span>
          <span className="h-px flex-1 bg-gradient-to-r from-zinc-300 to-transparent" />
        </Reveal>

        <Reveal>
          <div
            data-testid="nova-product-card"
            className="group relative overflow-hidden rounded-[28px] border border-zinc-800 bg-zinc-950 p-8 sm:p-12 lg:p-16 shadow-2xl shadow-blue-900/10"
          >
            {/* ambient glows */}
            <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full bg-blue-900/30 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="/nova-logo.png"
                  alt="Nova"
                  data-testid="nova-logo"
                  className="h-9 sm:h-10 w-auto"
                />
                <h2 className="mt-7 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
                  {nova.tagline}
                </h2>
                <p className="mt-5 max-w-xl text-base text-zinc-400 leading-relaxed">
                  {nova.description}
                </p>

                <a
                  href={nova.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="visit-nova-website-btn"
                  className="group/btn mt-8 inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium text-white rounded-full bg-blue-600 hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-lg shadow-blue-600/30"
                >
                  Visit Nova Website
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </div>

              <div className="lg:pl-8">
                <ul className="space-y-4">
                  {highlights.map((h, i) => (
                    <motion.li
                      key={h}
                      initial={{ opacity: 0, x: 16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4"
                    >
                      <span className="mt-0.5 grid place-items-center w-6 h-6 shrink-0 rounded-full bg-blue-600/25 text-blue-300">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm text-zinc-300 leading-relaxed">{h}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-6 flex flex-wrap gap-2">
                  {COMPLIANCE_FRAMEWORKS.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-blue-500/25 bg-blue-500/10 px-3 py-1 text-xs text-blue-300"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
